import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  HelpCircle,
  Plus,
  Minus,
  Mail,
  Phone,
  FileText,
} from "lucide-react";
import gsap from "gsap";

const HERO_IMAGE = "/assets/images/herobg.jpeg";

const faqs = [
  {
    category: "General",
    question: "What is the full meaning of OGFZA?",
    answer: "Oil & Gas Free Zones Authority, Nigeria.",
  },
  {
    category: "General",
    question: "What does OGFZA do?",
    answer:
      "The Oil & Gas Free Zones Authority is the government regulatory agency superintending over the operations of oil and gas free trade zones in Nigeria.",
  },
  {
    category: "Investment",
    question: "Who can invest?",
    answer: "Companies in the oil and gas sector and its value chain.",
  },
  {
    category: "Licensing",
    question: "Who can get a free zone plate number?",
    answer:
      "Only duly registered and operational enterprises can access the free zone licence plate at a fee stated in the OGFZA Regulations 2019. To view it on the website, kindly click on the download tab.",
  },
  {
    category: "Registration",
    question: "What are the requirements for registration?",
    answer:
      "Refer to the last page of the free zone application form. It can be accessed by clicking on the download tab.",
  },
  {
    category: "Operations",
    question: "How can I lease land for operations in the zones?",
    answer:
      "Land can be leased from any of the developers or concessionaires operating in the zones.",
  },
  {
    category: "Fees",
    question:
      "What is the fee for conducting corporate search on a free zone enterprise?",
    answer: "$200.",
  },
  {
    category: "Registration",
    question: "Can an offshore company register with OGFZA?",
    answer: "Yes. So long as they meet OGFZA registration requirements.",
  },
  {
    category: "Registration",
    question: "Is CAC certificate a requirement for registration?",
    answer:
      "No. CAC is no longer a requirement. Formation or incorporation of a free zone enterprise no longer requires CAC registration.",
  },
  {
    category: "Licensing",
    question: "How long does it take to acquire a free zone licence?",
    answer:
      "All categories of licences are issued within seven days from the date of submission of requisite documents and payment of prescribed fee.",
  },
  {
    category: "Licensing",
    question: "What is the duration of a free zone licence?",
    answer: "All licences expire on 31 December of the year.",
  },
  {
    category: "Vendor Registration",
    question:
      "How can I be accredited as a vendor or service provider in the free zone?",
    answer:
      "By applying, completing and submitting the requisite forms, as well as paying the prescribed fees.",
  },
  {
    category: "Contact",
    question: "Do you have official channels for enquiry?",
    answer:
      "Yes. Email: contact@ogfza.gov.ng. Phone lines: +234 909 111 1184, +234 909 111 1185.",
  },
  {
    category: "Contact",
    question: "Where are your physical addresses?",
    answer:
      "OGFZA has several offices in the country, and their addresses are available on the website.",
  },
  {
    category: "Operations",
    question:
      "Does a company have to be located within the free zone to operate as a free zone enterprise?",
    answer: "Yes.",
  },
  {
    category: "Renewal",
    question:
      "What are the requirements for renewal of a free zone enterprise?",
    answer:
      "Section 24 of the Regulations 2019 states that, upon expiration, a licence shall be renewed on payment of prescribed fees, payment of any outstanding amount due to the Authority, presentation of any other documents, returns or information required by the Authority, and presentation of any acceptable appraisal report in the case of a free zone enterprise.",
  },
  {
    category: "Licensing",
    question:
      "Can a free zone enterprise modify or change its approved activities after licensing?",
    answer:
      "Yes. Section 25 of the Regulations 2019 provides that, upon the application of a licensee, the Authority may amend its licence on the payment of prescribed fees.",
  },
  {
    category: "Immigration",
    question:
      "How does a free zone enterprise process a work permit for Free Zone expatriates?",
    answer:
      "With the new Immigration Regime introduced in 2025, the entire process can be done online at cerpac.immigration.gov.ng, provided the enterprise possesses a valid free zone licence.",
  },
  {
    category: "Fees",
    question: "What is the cost of a Developer Licence?",
    answer: "$200,000. Renewal fee: $50,000.",
  },
  {
    category: "Fees",
    question: "What is the cost of an Enterprise Licence?",
    answer: "$25,000. Renewal fee: $25,000.",
  },
  {
    category: "Fees",
    question: "What is the cost of Special Activity Licence?",
    answer:
      "Refineries: $250,000, Renewal: $50,000. Petrochemical: $100,000, Renewal: $40,000. Assembly Plants: $100,000, Renewal: $40,000. Independent Power Plants: $100,000, Renewal: $40,000. Fertilizer Plant: $100,000, Renewal: $40,000. Tank Farm: $200,000, Renewal: $40,000. Any other Special Activity: $100,000, Renewal: $40,000.",
  },
  {
    category: "HSE",
    question:
      "What are the primary requirements for issuance of an annual HSE Compliance or Assurance Certificate?",
    answer:
      "Issuance of the certificate is subject to completeness of the HSE documentation, namely compliance inspections, on-site assessment and compliance evaluation.",
  },
  {
    category: "HSE",
    question: "What is the turnaround time for HSE permit application?",
    answer: "Processing time is five to seven working days.",
  },
  {
    category: "HSE",
    question:
      "When can one apply for renewal of Annual HSE Certificate?",
    answer:
      "The renewal process should be initiated 30 days before the certificate expiration date.",
  },
  {
    category: "Cargo",
    question:
      "How long does it take to process urgent pre-release of cargoes?",
    answer: "48 hours, which is two business days.",
  },
  {
    category: "Cargo",
    question:
      "How long does it take to transfer cargoes from quayside to stacking area?",
    answer: "24 hours after receiving complete documentation.",
  },
  {
    category: "HSE",
    question:
      "Is annual HSE Certificate mandatory for all categories of entities within the free zone?",
    answer: "Yes. It is a prerequisite for renewal of operating licence.",
  },
  {
    category: "HSE",
    question: "How long does an HSE physical site inspection take?",
    answer:
      "Two to four hours, depending on the size of the facility.",
  },
];

const categories = ["All", ...new Set(faqs.map((faq) => faq.category))];

const FAQ = () => {
  const pageRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredFaqs =
    activeCategory === "All"
      ? faqs
      : faqs.filter((faq) => faq.category === activeCategory);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".hero-badge", { opacity: 0, y: 30, duration: 0.8 });
      gsap.from(".hero-title", { opacity: 0, y: 80, duration: 1.1 });
      gsap.from(".hero-text", {
        opacity: 0,
        y: 40,
        duration: 0.9,
        delay: 0.25,
      });
      gsap.from(".faq-card", {
        opacity: 0,
        y: 60,
        duration: 1,
        stagger: 0.08,
      });

      gsap.to(".hero-orb-one", {
        y: 40,
        x: -25,
        repeat: -1,
        yoyo: true,
        duration: 6,
        ease: "sine.inOut",
      });

      gsap.to(".hero-orb-two", {
        y: -35,
        x: 25,
        repeat: -1,
        yoyo: true,
        duration: 7,
        ease: "sine.inOut",
      });
    }, pageRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [activeCategory]);

  return (
    <main ref={pageRef}>
      <section className="relative overflow-hidden">
        <div className="relative min-h-[640px] bg-[#04160a] pt-28 pb-28 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="OGFZA FAQ"
            className="absolute inset-0 w-full h-full object-cover"
          />

          <div className="absolute inset-0 bg-[#04160a]/80" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#04160a]/95 via-[#06183f]/75 to-[#ff6600]/35" />
          <div className="hero-orb-one absolute -top-24 -left-24 w-[460px] h-[460px] rounded-full bg-[#06183f]/35 blur-3xl" />
          <div className="hero-orb-two absolute -bottom-28 -right-24 w-[460px] h-[460px] rounded-full bg-[#ff6600]/30 blur-3xl" />
          <div className="absolute inset-0 opacity-[0.08] bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:80px_80px]" />

          <div className="relative max-w-7xl mx-auto px-6 text-center">
            <div className="hero-badge inline-flex items-center gap-3 mb-5">
              <span className="w-10 h-[3px] bg-[#ff6600]" />
              <span className="text-[#ff6600] font-bold tracking-[0.2em] text-sm uppercase">
                Contact Help
              </span>
            </div>

            <h1
              className="hero-title text-5xl md:text-7xl lg:text-[82px] font-black text-white leading-[1.02]"
              style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
            >
              Frequently Asked Questions
            </h1>

            <p className="hero-text text-white/85 max-w-3xl mx-auto mt-6 leading-relaxed text-[17px]">
              Find answers to common questions on OGFZA registration, licensing,
              Free Zone operations, fees, renewals, HSE permits, cargo
              processing and customer support.
            </p>

            <div className="hero-text flex items-center justify-center gap-2 mt-6 text-sm text-white/80 flex-wrap">
              <Link to="/" className="hover:text-[#ff6600] transition-colors">
                Home
              </Link>
              <ChevronRight size={14} />
              <span className="text-[#ff6600]">FAQ</span>
            </div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-20">
          <div className="grid lg:grid-cols-[0.75fr_1.25fr] gap-8">
            <aside className="contact-card bg-[#06183f] rounded-[34px] p-8 text-white h-fit">
              <HelpCircle size={42} className="text-[#ff6600] mb-5" />

              <h2
                className="text-3xl font-black mb-4"
                style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
              >
                Need More Help?
              </h2>

              <p className="text-white/75 leading-relaxed mb-6">
                For enquiries not covered here, contact OGFZA through the
                official customer service channel or submit a regulatory
                complaint if your issue requires tracking.
              </p>

              <div className="space-y-4 text-sm mb-8">
                <p className="flex gap-3">
                  <Mail size={18} className="text-[#ff6600] shrink-0" />
                  contact@ogfza.gov.ng
                </p>

                <p className="flex gap-3">
                  <Phone size={18} className="text-[#ff6600] shrink-0" />
                  +234 909 111 1184, +234 909 111 1185
                </p>

                <p className="flex gap-3">
                  <FileText size={18} className="text-[#ff6600] shrink-0" />
                  Use the Downloads section for forms and regulations.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <Link
                  to="/contact-help/customer-service"
                  className="px-5 py-3 rounded-2xl bg-white text-[#06183f] font-black"
                >
                  Contact Us
                </Link>

                <Link
                  to="/contact-help/complaints"
                  className="px-5 py-3 rounded-2xl bg-[#ff6600] text-white font-black"
                >
                  Submit Complaint
                </Link>
              </div>
            </aside>

            <div>
              <div className="flex flex-wrap gap-3 mb-8">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`px-5 py-3 rounded-full font-black text-sm transition-all ${
                      activeCategory === category
                        ? "bg-[#06183f] text-white"
                        : "bg-white text-[#06183f] border border-slate-200 hover:border-[#ff6600]"
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                {filteredFaqs.map((faq, index) => {
                  const open = activeIndex === index;

                  return (
                    <div
                      key={`${faq.question}-${index}`}
                      className="faq-card bg-white rounded-[26px] border border-slate-100 shadow-[0_18px_45px_rgba(0,0,0,0.08)] overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={() => setActiveIndex(open ? null : index)}
                        className="w-full flex items-start justify-between gap-5 text-left p-6"
                      >
                        <div>
                          <span className="inline-flex mb-3 px-3 py-1 rounded-full bg-[#ff6600]/10 text-[#ff6600] text-xs font-black">
                            {faq.category}
                          </span>

                          <h3
                            className="text-xl md:text-2xl font-black text-[#06183f]"
                            style={{ fontFamily: "'Alegreya Sans', sans-serif" }}
                          >
                            {faq.question}
                          </h3>
                        </div>

                        <span className="w-10 h-10 rounded-full bg-[#06183f]/10 text-[#06183f] flex items-center justify-center shrink-0">
                          {open ? <Minus size={20} /> : <Plus size={20} />}
                        </span>
                      </button>

                      {open && (
                        <div className="px-6 pb-6">
                          <div className="border-t border-slate-100 pt-5">
                            <p className="text-slate-600 leading-[1.9]">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default FAQ;